import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'
import type { Namespace, TermKey } from '@/i18n/termKeys'

export type TermProfile = {
  id: string // composite locale code, e.g. 'en-clinical'
  label: string
  lang: string
  variant: string
  fallback: string | null // composite code it inherits from (null = base)
  isBase: boolean // the protected canonical base
}

export type CreateProfileInput = {
  lang: string
  variant: string
  label: string
  fallback?: string
}

/** Flat resolved term map for a (locale, namespace). */
export type TermMap = Record<string, string>

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Attach the bearer token like every other api client (writes are @AuthOnly).
  const res = await authFetch(`${BASE}${path}`, {
    headers: authHeaders(init?.headers),
    ...init,
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    throw new Error(`API ${init?.method ?? 'GET'} ${path} failed (${res.status}): ${text}`)
  }
  return res.json() as Promise<T>
}

export const terminologyApi = {
  listProfiles: () =>
    request<{ profiles: TermProfile[] }>('/terms/profiles').then((r) => r.profiles),

  getNamespace: (locale: string, namespace: Namespace) =>
    request<TermMap>(`/terms/${locale}/${namespace}`),

  saveOverrides: <N extends Namespace>(
    locale: string,
    namespace: N,
    values: Partial<Record<TermKey<N>, string>>,
  ) =>
    request<{ version: string; terms: TermMap }>(`/terms/${locale}/${namespace}`, {
      method: 'PUT',
      body: JSON.stringify({ values }),
    }),

  createProfile: (input: CreateProfileInput) =>
    request<TermProfile>('/terms/profiles', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  updateProfile: (id: string, patch: { label?: string; fallback?: string }) =>
    request<TermProfile>(`/terms/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  deleteProfile: (id: string) =>
    authFetch(`${BASE}/terms/${id}`, { method: 'DELETE', headers: authHeaders() }).then(async (res) => {
      if (!res.ok) {
        handleUnauthorized(res.status)
        const text = await res.text().catch(() => res.statusText)
        throw new Error(`API DELETE /terms/${id} failed (${res.status}): ${text}`)
      }
    }),
}
